import { Flex } from '@mantine/core';
import { useMemo } from 'react';
import { LegendProps } from 'recharts';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import { LineChartLegendItem } from '../../../strategy-v2/initiative-performance/components/line-chart/line-chart-legend-item';

type Props = LegendProps & {
  hoveredItem: string | null;
  setHoveredItem: (item: string | null) => void;
};

export const Legend = ({ hoveredItem, setHoveredItem, ...props }: Props) => {
  const { payload } = props as any;

  // Selected trend-lines from each Financials sub-filter
  const allocationTrendLines = useFinancialsStore((state) => state.filters.allocationOfCosts.selectedTrendLines);
  const initiativeTrendLines = useFinancialsStore((state) => state.filters.costByInitiative.selectedTrendLines);
  const cvoTrendLines = useFinancialsStore((state) => state.filters.costVersusOutput.selectedTrendLines);

  // Build a fast-lookup Set (stable between renders unless the arrays actually change)
  const trendLineSet = useMemo(
    () => new Set([...(allocationTrendLines || []), ...(initiativeTrendLines || []), ...(cvoTrendLines || [])]),
    [allocationTrendLines, initiativeTrendLines, cvoTrendLines],
  );

  // Exclude trend entries whose base series is not selected
  const filteredPayload = useMemo(() => {
    if (!payload) return [];

    return (payload as any[]).filter((entry: any) => {
      const itemId: string | undefined = entry.id || entry.value;
      if (!itemId) return false;

      // If this is a trend item, only keep it when its base series is selected
      if (itemId.endsWith('_trend')) {
        const baseId = itemId.replace(/_trend$/, '');
        return trendLineSet.has(baseId);
      }

      // Regular (non-trend) series are always shown
      return true;
    });
  }, [payload, trendLineSet]);

  if (!filteredPayload || filteredPayload.length === 0) {
    return null;
  }

  return (
    <Flex justify="center" align="center" gap="md" h="100%">
      {filteredPayload.map((entry: any, index: number) => {
        const color = entry.color;
        const itemId = entry.id || entry.value || `item-${index}`;
        const isHovered = hoveredItem === null || hoveredItem === itemId;

        // Format the display label for trend items
        const displayLabel =
          entry.value && typeof entry.value === 'string' && entry.value.includes('_trend')
            ? entry.value.replace('_trend', ' Trend')
            : entry.value;

        return (
          <LineChartLegendItem
            key={itemId}
            value={entry.value}
            label={displayLabel}
            color={color || ''}
            isHovered={isHovered}
            onMouseEnter={() => setHoveredItem(itemId)}
            onMouseLeave={() => setHoveredItem(null)}
          />
        );
      })}
    </Flex>
  );
};
